import type { ComponentProps } from 'react';
import React from 'react';
import type { ScrollViewProps, StyleProp, ViewStyle } from 'react-native';
import { View, ScrollView, StyleSheet, Platform } from 'react-native';

import { theme } from '@garnish/constants';
import { useResponsive } from '../../hooks';
import { Container as GarnishContainer } from '../Container';
import type { useReCaptcha } from '../ReCaptcha';
import { ReCaptcha } from '../ReCaptcha';
import { IconLink } from '../Icon';

// ────────────────────────────────────────────────────────────────────────────────

export const Container = (props: ContainerProps) => {
  const { currentBreakpoint, minWidth } = useResponsive();
  const {
    children,
    style,
    contentStyle,
    reCaptchaRef,
    reCaptchaMachine,
    onClose,
    onRecaptchaVerify,
    onRecaptchaError,
    removeSpacing = false,
    ...restProps
  } = props;

  const showCloseBtnBlock = currentBreakpoint.isMobile;
  const shouldRenderRecaptcha =
    reCaptchaRef !== undefined && onRecaptchaVerify !== undefined;

  // ─── STYLES ─────────────────────────────────────────────────────────

  const wrapperStyle = [
    styles.wrapper,
    !currentBreakpoint.isMobile && styles.wrapperTabletUp,
    style,
  ];
  const content = [
    styles.content,
    !currentBreakpoint.isMobile && styles.contentTabletUp,
    contentStyle,
  ];
  const recaptchaStyle = Platform.OS === 'web' && [
    styles.recaptchaWeb,
    minWidth.tablet && styles.recaptchaWebSM,
  ];

  return (
    <ScrollView
      contentContainerStyle={wrapperStyle}
      {...restProps}
      style={content}
    >
      {showCloseBtnBlock && onClose ? (
        <GarnishContainer style={styles.header}>
          <IconLink name="IconClose" onPress={onClose} />
        </GarnishContainer>
      ) : null}

      <GarnishContainer removeSpacing={removeSpacing} size="medium">
        {children}
      </GarnishContainer>

      {shouldRenderRecaptcha ? (
        <View style={recaptchaStyle}>
          <ReCaptcha
            ref={reCaptchaRef}
            machine={reCaptchaMachine}
            onVerify={onRecaptchaVerify}
            onError={onRecaptchaError}
          />
        </View>
      ) : null}
    </ScrollView>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ContainerProps = {
  readonly onClose?: () => void;
  readonly contentStyle?: StyleProp<ViewStyle>;
  readonly removeSpacing?: boolean;
  readonly reCaptchaRef?: ReturnType<typeof useReCaptcha>['reCaptchaRef'];
  readonly reCaptchaMachine?: ComponentProps<typeof ReCaptcha>['machine'];
  readonly onRecaptchaVerify?: ComponentProps<typeof ReCaptcha>['onVerify'];
  readonly onRecaptchaError?: ComponentProps<typeof ReCaptcha>['onError'];
} & ScrollViewProps;

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flexGrow: 1,
    paddingBottom: theme.spacing.large,
  },
  wrapperTabletUp: {
    justifyContent: 'center',
  },
  content: {
    backgroundColor: theme.colors.OATMEAL,
  },
  contentTabletUp: {
    paddingVertical: theme.spacing.xxlarge,
  },
  header: {
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'flex-end',
    marginBottom: theme.spacing.medium,
  },
  recaptchaWeb: {
    marginTop: theme.spacing.large,
  },
  recaptchaWebSM: {
    height: 70,
  },
});
